import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  FileText,
  Download,
  Lock,
  CheckCircle2,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DocumentCard } from "@/components/DocumentCard";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

const HERO_IMG =
  "https://images.unsplash.com/photo-1632152133952-98b268dc4b86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBzaWduYXR1cmUlMjBsZWdhbCUyMGNvbnRyYWN0JTIwZG9jdW1lbnQlMjBwYXBlciUyMGRlc2t8ZW58MHx8fHwxNzkwMzM3MzM1fDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { num: "1", title: "Alegi documentul", desc: "Selectezi tipul de document de care ai nevoie." },
  { num: "2", title: "Completezi câteva informații", desc: "Răspunzi la un formular scurt și ghidat." },
  { num: "3", title: "Plătești 10 lei", desc: "Plată unică, securizată, fără cont și fără abonament." },
  { num: "4", title: "Descarci documentul", desc: "Primești documentul în format PDF și Word." },
];

export default function Home() {
  useSEO(
    "ActeSimplu – Documente personalizate în câteva minute",
    "Generează online documente administrative personalizate. Completezi câteva informații și primești documentul în PDF și Word. Plată unică 10 lei."
  );
  const [documents, setDocuments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/documents").then((r) => setDocuments(r.data)).catch(() => {});
  }, []);

  const goToDocs = () => {
    const el = document.getElementById("documente");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/60 to-transparent dark:from-blue-950/20" />
        <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-up">
              <Badge className="mb-5 bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-300 rounded-full px-3 py-1 font-semibold">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Generare instant • PDF și Word
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-slate-900 dark:text-white">
                Documentul de care ai nevoie,{" "}
                <span className="text-blue-600">în câteva minute.</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-xl">
                Răspunzi la câteva întrebări, verifici informațiile și primești
                documentul personalizat în format PDF și Word.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="hero-cta"
                  onClick={goToDocs}
                  size="lg"
                  className="rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base px-8 h-14 shadow-lg shadow-blue-200 dark:shadow-none"
                >
                  Alege documentul – 10 lei
                  <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
              </div>
              <p className="mt-4 text-sm text-slate-500">Plată unică. Fără abonament.</p>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
                {[
                  "Formate PDF și DOCX",
                  "Plată securizată prin Stripe",
                  "Fără cont obligatoriu",
                ].map((t) => (
                  <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" /> {t}
                  </span>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="relative hidden lg:block"
            >
              <div className="rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-2xl shadow-slate-200/60 dark:shadow-none">
                <img src={HERO_IMG} alt="Document administrativ" className="w-full h-[440px] object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 p-4 flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600">
                  <CheckCircle2 className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Document gata!</p>
                  <p className="text-xs text-slate-500">PDF + Word descărcabile</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CUM FUNCȚIONEAZĂ */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
            Simplu și rapid
          </span>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
            Cum funcționează
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="relative p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
            >
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-600 text-white font-heading font-bold text-lg mb-4">
                {s.num}
              </span>
              <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-1">{s.title}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* DOCUMENTE */}
      <section id="documente" className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 sm:py-24 scroll-mt-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
            Catalog
          </span>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
            Alege documentul de care ai nevoie
          </h2>
          <p className="mt-3 text-slate-500 dark:text-slate-400">
            Fiecare document costă 10 lei. Plată unică, fără abonament.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {documents.map((doc, i) => (
            <DocumentCard key={doc.slug} doc={doc} index={i} />
          ))}
        </div>
      </section>

      {/* SIGURANȚĂ */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 sm:py-24">
        <div className="rounded-3xl bg-slate-900 dark:bg-slate-800 p-8 sm:p-14 text-white">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-400">
              Siguranță și confidențialitate
            </span>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold">
              Datele tale sunt în siguranță
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-3 gap-8">
            {[
              { icon: Lock, title: "Plată securizată", desc: "Plata este procesată de un furnizor extern (Stripe). Nu stocăm datele cardului tău." },
              { icon: ShieldCheck, title: "Transmitere securizată", desc: "Datele sunt transmise securizat și folosite doar pentru generarea documentului." },
              { icon: Download, title: "Ștergere automată", desc: "Documentele temporare sunt șterse automat după perioada configurată (72 de ore)." },
            ].map((f) => (
              <div key={f.title}>
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 text-blue-300 mb-4">
                  <f.icon className="w-5 h-5" />
                </span>
                <h3 className="font-heading font-semibold text-lg mb-1">{f.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 p-5 rounded-2xl bg-white/5 border border-white/10">
            <p className="text-xs text-slate-300 leading-relaxed">
              <strong className="text-white">Disclaimer:</strong> ActeSimplu generează
              modele de documente pe baza informațiilor introduse de utilizator.
              Documentele nu constituie consultanță juridică, fiscală sau profesională.
              Verifică informațiile înainte de utilizare.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
